import type { AssetManifest } from '@kenocyber/shared';
import { GachaPanel } from './GachaPanel';
import { MarketPanel } from './MarketPanel';
import { StorePanel } from './StorePanel';
import { TradePanel } from './TradePanel';

/**
 * The floating panels that may not coexist: trade, market, gacha and the store.
 *
 * ## Why they are one component and not two booleans
 *
 * They used to be `tradeOpen` and `marketOpen`, two independent flags, and two
 * independent flags can both be true. When they were, React mounted BOTH
 * panels: two backdrops, two close buttons, two `Escape` listeners and the
 * market's content painted over the trade's. Nothing in the code said the two
 * screens were alternatives, so nothing stopped them from overlapping.
 *
 * One value of a union type says it. `overlay` is `'trade'`, `'market'` or
 * `null`, so "both" is not a state that exists to be reached - not by a double
 * click, not by a race, not by a late response. The inactive panel is really
 * unmounted rather than hidden: an invisible `TradePanel` would still poll the
 * open trade every three seconds and still answer `Escape`.
 *
 * ## Unmounting is what resets them
 *
 * Because the panel is destroyed, so is everything it was holding - a pending
 * purchase confirmation, a half-typed price, a stale error. Re-opening starts
 * clean, which is the behaviour a player expects from a screen they closed.
 *
 * ## A closed panel cannot re-open itself
 *
 * The state lives above this component and neither panel can write to it. A
 * request that resolves after the player closed the market therefore lands on
 * a component that no longer exists - a no-op - and can never bring the screen
 * back on its own.
 *
 * The hunt keeps running behind whichever one is open: these panels float over
 * the arena and never touch the canvas.
 */

/** The panels that are alternatives to each other. Adding one to this union
 * is all it takes to make it exclusive with the others. */
export type FloatingOverlay = 'trade' | 'market' | 'gacha' | 'store' | 'maps' | 'mining';

interface FloatingOverlaysProps {
  readonly overlay: FloatingOverlay | null;
  /** The market needs it to draw art; the trade does not. */
  readonly manifest: AssetManifest | null;
  readonly onClose: () => void;
  readonly onSettled: () => void;
}

export function FloatingOverlays({ overlay, manifest, onClose, onSettled }: FloatingOverlaysProps) {
  if (overlay === 'trade') {
    return <TradePanel onClose={onClose} onSettled={onSettled} />;
  }
  if (overlay === 'market' && manifest) {
    return <MarketPanel manifest={manifest} onClose={onClose} onSettled={onSettled} />;
  }
  if (overlay === 'gacha' && manifest) {
    return <GachaPanel manifest={manifest} onClose={onClose} onSettled={onSettled} />;
  }
  /*
   * The store needs no manifest: it draws prices and a QR the server sent, not
   * item art. Unmounting it is what stops its polling - a hidden panel would
   * keep asking about an order every four seconds for as long as the tab lived.
   */
  if (overlay === 'store') {
    return <StorePanel onClose={onClose} onSettled={onSettled} />;
  }
  return null;
}

/**
 * What clicking an overlay's own button does.
 *
 * Clicking the open one closes it; clicking the other one REPLACES it. There
 * is no path through this that returns two panels, which is the whole point.
 */
export function toggleOverlay(
  current: FloatingOverlay | null,
  clicked: FloatingOverlay,
): FloatingOverlay | null {
  return current === clicked ? null : clicked;
}
